import {
	Checkbox,
	InputAdornment,
	MenuItem,
	Grid,
	IconButton,
	Typography,
	TextField,
	Divider,
	Stack,
	ListSubheader,
} from '@mui/material';

import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { debounce, cloneDeep } from 'lodash';
import { FixedSizeList as List } from 'react-window';

import { ReactComponent as DrawerClose } from 'common/assets/images/drawer-close.svg';
import { ReactComponent as AllCloseIcon } from 'common/assets/images/iconclose.svg';
import {
	BUTTON_LABELS,
	DYNAMIC_LIST_ITEM_SIZE,
	FILTER_LABELS,
	MINIMUM_CHARACTER_MESSAGE,
	NO_OPTIONS_MESSAGE,
} from 'common/constants';
import { FILTER_TYPE } from 'common/constants/CitationDetails';
import { RootState } from 'common/store/AppStore';
import {
	setClearAllDates,
	setDueDateValue,
	setIsNavigateToDetails,
	setSelectedDateArray,
	setSelectedDueDate,
	setSelectedEffectiveDate,
} from 'common/store/reducer/horizonScanReducer';
import { toSentenceCase } from 'common/utils/utilFunctions';

import { ReactComponent as SearchOutlined } from '../../assets/images/search-outline.svg';

import AccordionComp from '../Accordion/AccordionComp';
import ButtonComp from '../Button/ButtonComp';
import { Loader } from '../Loader/Loader';
import CustomDateRangeComponent from '../RangeDatePickerComp/RangeDatePickerComp';

import AdvancedFilterListItems from './AdvancedFilterListItems';

import styles from './AdvancedFilters.module.scss';

const { imageColor } = styles;

export interface IAdvancedFiltersOptions {
	filterKey: string;
	filterValues: IAdvancedFilterValueOptions[];
	selectedOptions?: IAdvancedFilterValueOptions[];
	filterTitle: string;
	isLoading?: boolean;
	searchFlag?: boolean;
	searchText?: string;
	subOptions?: IAdvancedFiltersOptions[];
}
export interface IAdvancedFilterValueOptions {
	label: any;
	value: string;
	checked?: boolean;
	type?: string;
	disabled?: boolean;
}

interface IAdvancedFiltersProps {
	filterOptionsData: IAdvancedFiltersOptions[];
	onClose: () => void;
	onSearchInputChange?: (
		searchString: string,
		type: string,
		name: string,
	) => void;
	applyFiltersHandler: (data: IAdvancedFilterValueOptions[]) => void;
	handleClearFilter: () => void;
	filterDataLoading: boolean;
	selectedListOptions: IAdvancedFilterValueOptions[];
	onCancel: () => void;
	moduleType: boolean;
	customDateDetailKeys?: string[];
	hideSearchFieldKeys?: string[];
	onAssignedToMeChecked?: (assignedToMe: boolean) => void;
	showingAssignedToMe?: boolean;
	customShowOnlyAssignedMessage?: string;
	filterCount?: number;
}
const AdvancedFilters = (props: IAdvancedFiltersProps) => {
	const {
		onClose,
		applyFiltersHandler,
		filterOptionsData,
		filterDataLoading,
		selectedListOptions,
		onCancel,
		handleClearFilter,
		moduleType = false,
		customDateDetailKeys = [],
		hideSearchFieldKeys = [],
		onAssignedToMeChecked,
		showingAssignedToMe,
		onSearchInputChange,
		customShowOnlyAssignedMessage,
		filterCount,
	} = props;
	const originalData = cloneDeep(filterOptionsData);
	const [selectedAccordians, setSelectedAccordians] = React.useState<
		IAdvancedFiltersOptions[]
	>([]);
	const [selectedInnerAccordians, setSelectedInnerAccordians] = useState<
		IAdvancedFiltersOptions[]
	>([]);
	const [selectedOptions, setSelectedOptions] =
		React.useState<IAdvancedFilterValueOptions[]>(selectedListOptions);
	const [searchText, setSearchText] = React.useState<string>('');
	const [allOptions, setAllOptions] =
		React.useState<IAdvancedFiltersOptions[]>(filterOptionsData);
	const [isChanged, setIsChanged] = useState<boolean>(false);
	const [assignedToMeChecked, setAssignedToMeChecked] = useState<boolean>(
		showingAssignedToMe ?? false,
	);
	const dispatch = useDispatch();
	const {
		drawerCloseIconColor,
		dividerColor,
		checkboxCheckedColor,
		checkboxUncheckedColor,
	} = styles;
	const applyFilterEnabled = useSelector(
		(state: RootState) => state.taxonomy.applyFilterEnabled,
	);

	const { JURISDICTION_TYPE } = FILTER_TYPE;

	const onSelectDateRange = (
		option: any,
		res: any,
		isAllOptionSelected?: boolean,
	) => {
		const temp = {
			checked: false,
			label: option,
			type: res.filterKey,
			value: option,
		};

		setSelectedOptions((prevState: any) => {
			let prevData = [...prevState];
			const existingIndex = prevData.findIndex(
				(ele: any) => ele.type === res.filterKey,
			);
			if (isAllOptionSelected) {
				prevData = prevData.filter((res) => res.type !== 'activityDate');
				setIsChanged(true);
			} else if (existingIndex >= 0) {
				const updatedElement = {
					...prevData[existingIndex],
					value: option,
					label: option,
				};

				prevData[existingIndex] = updatedElement;
			} else {
				prevData.push(temp);
			}
			return prevData;
		});
	};

	const onApplyFilter = () => {
		applyFiltersHandler(selectedOptions);
		dispatch(setIsNavigateToDetails(false));
	};

	const selectedLabels: any[] = selectedAccordians.map(
		(ele: any) => ele.filterKey,
	);
	const selectedSubLabels: any[] = selectedInnerAccordians.map(
		(ele: any) => ele.filterTitle,
	);
	const handleAccordianChange = (event: React.SyntheticEvent, res: any) => {
		setAllOptions([...originalData]);
		setSearchText('');
		if (selectedLabels.includes(res.filterKey)) {
			setSelectedAccordians((val) =>
				val.filter((accordian) => accordian.filterKey !== res.filterKey),
			);
		} else {
			setSelectedAccordians([...selectedAccordians, res]);
		}
	};

	const handleInnerAccordians = (event: React.SyntheticEvent, res: any) => {
		setAllOptions([...originalData]);
		setSearchText('');
		if (selectedSubLabels.includes(res.filterTitle)) {
			setSelectedInnerAccordians((val) =>
				val.filter((accordian) => accordian.filterTitle !== res.filterTitle),
			);
		} else {
			setSelectedInnerAccordians([...selectedInnerAccordians, res]);
		}
	};
	const handleChange = (
		res: IAdvancedFilterValueOptions,
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const label = typeof res.label === 'object' ? res.label.value : res.label;
		setIsChanged(true);

		if ((event.target as HTMLInputElement).checked) {
			setSelectedOptions((prev) => [...prev, { ...res, label, checked: true }]);
		} else {
			setSelectedOptions(selectedOptions.filter((val) => val?.label !== label));
		}
	};

	useEffect(() => {
		setAllOptions(filterOptionsData);
	}, [filterOptionsData]);

	const handleAssignedToMeChecked = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setIsChanged(true);
		const isChecked = event.target.checked;
		setAssignedToMeChecked(isChecked);
		if (onAssignedToMeChecked) {
			onAssignedToMeChecked(isChecked);
		}
	};

	const handleFilterValue = (
		searchVal: any,
		ele: any,
		allData: IAdvancedFilterValueOptions[],
	) => {
		if (searchVal === '') {
			ele.filterValues = allData;
		} else {
			const searchList = allData.filter((ele: any) => {
				const { value } = ele;
				return value?.toLowerCase().includes(searchVal.toLowerCase());
			});
			ele.filterValues = searchList;
		}
	};

	const handleSearch = (e: any, data: any) => {
		if (onSearchInputChange) {
			setSearchText(e?.target?.value);
			onSearchInputChange(e?.target?.value, data?.filterKey, data?.filterTitle);
			return;
		}
		const searchVal = e?.target?.value;
		setSearchText(searchVal);
		const filteredValues = originalData?.filter(
			(ele: any) => ele?.filterKey === data?.filterKey,
		);
		setAllOptions((prevState: any) => {
			const prevData = [...prevState];
			prevData.forEach((ele: any) => {
				if (ele?.filterKey === data?.filterKey) {
					const allData = filteredValues[0]?.filterValues;
					handleFilterValue(searchVal, ele, allData);
					if (ele.filterValues.length === 0) {
						ele.searchFlag = searchVal !== '';
					} else {
						ele.searchFlag = false;
					}
				} else {
					ele.searchFlag = false;
				}
			});
			return prevData;
		});
	};
	const onAllAccordionsClose = () => {
		setSelectedAccordians([]);
	};
	const onClearFilterValues = () => {
		setSearchText('');
		containerSearchRefs.current.forEach((containerRef: any) => {
			containerRef.value = '';
		});
		setSelectedOptions([]);
		setSelectedAccordians([]);
		handleClearFilter();
		dispatch(setDueDateValue([]));
		setAssignedToMeChecked(false);
		dispatch(setSelectedDateArray([]));
		dispatch(setSelectedDueDate(''));
		dispatch(setSelectedEffectiveDate(''));
		dispatch(setClearAllDates());
		const customDateRangeOptions = filterOptionsData.filter((option) =>
			customDateDetailKeys.includes(option.filterKey),
		);
		customDateRangeOptions.forEach((option) => {
			const selectedOptionIndex = selectedOptions.findIndex(
				(selectedOption) => selectedOption.label === option.filterKey,
			);
			if (selectedOptionIndex !== -1) {
				const updatedSelectedOptions = [...selectedOptions];
				updatedSelectedOptions.splice(selectedOptionIndex, 1);
				setSelectedOptions(updatedSelectedOptions);
			}
		});
	};
	const handleCancel = () => {
		setSelectedAccordians([]);
		setSelectedOptions([]);
		onCancel();
	};

	const containerSearchRefs = React.useRef<any>([]);

	/**
	 * Returns the count of selected keys based on the provided key and type.
	 * @param key - The key to filter the selected options.
	 * @param type - The type to filter the selected options.
	 * @returns A JSX element displaying the key and the count of selected options.
	 */
	const getSelectedKeyCount = (res: IAdvancedFiltersOptions) => {
		const key = res.filterTitle;
		const type = res.filterKey;
		let count = 0;
		if (res.subOptions && res.subOptions.length > 0) {
			res.subOptions.forEach((element) => {
				const options = selectedOptions.filter(
					(ele: any) =>
						ele?.type === element.filterKey &&
						ele?.value === element.filterTitle,
				).length;
				count += options;
			});
		} else {
			const selectedKey: IAdvancedFilterValueOptions[] = selectedOptions.filter(
				(ele: any) =>
					ele?.type === JURISDICTION_TYPE
						? ele?.type === type && ele?.value === key
						: ele?.type === type,
			);
			count = selectedKey.length;
		}

		const value = count === 0 ? '' : `(${count})`;

		return (
			<div>
				{key}&nbsp;
				{value}
			</div>
		);
	};

	const renderTextFieldSearch = (ele: any, index: number) => (
		<TextField
			inputRef={(el) => {
				containerSearchRefs.current[index] = el;
			}}
			className={styles['advancedfilters-search-field']}
			autoFocus
			defaultValue={searchText}
			name="search-field"
			id="search-field"
			placeholder="Search"
			fullWidth
			onChange={(e) => {
				debouncedSearchHandler(e, ele);
			}}
			data-testid="search-field-main"
			InputProps={{
				inputProps: {
					'data-testid': 'search-field',
				},
				startAdornment: (
					<InputAdornment position="start">
						<SearchOutlined className={styles['input-search-icon']} />
					</InputAdornment>
				),
			}}
		/>
	);

	const renderClassName = (ele: any) =>
		ele?.filterValues.length === 0 && ele?.selectedOptions?.length === 0
			? getNoDataClassName(ele?.searchFlag || false)
			: styles['advancedfilters-scroll-container'];

	const getNoDataClassName = (searchFlag: boolean) =>
		searchFlag
			? styles['advancedfilters-accordion-maincontent-nodata-div']
			: styles['advancedfilters-accordion-maincontent-empty-div'];

	const debouncedSearchHandler = debounce(handleSearch, 500);

	const selectedOptionsLabels = selectedOptions.map((res: any) => res?.label);

	const selectedOptionsLabelsType = selectedOptions.map(
		(res: any) => res?.type,
	);

	const dividerRow = () => (
		<Grid item xs={12} lg={12} md={12} sm={12} mt={1}>
			<Divider
				sx={{
					'&.MuiDivider-root': {
						borderColor: dividerColor,
					},
				}}
			/>
		</Grid>
	);

	const returnCount = () => {
		let selectedOptionsCount = selectedOptions.length - (filterCount || 0);
		if (selectedOptionsCount < 0) {
			selectedOptionsCount = 0;
		}
		return selectedOptionsCount !== 0 ? `(${selectedOptionsCount})` : '';
	};

	const summaryElement = (ele: IAdvancedFiltersOptions) => (
		<Typography
			data-testid="doc-title"
			className={styles['advancedfilters-accordion-title']}
		>
			{getSelectedKeyCount(ele)}
		</Typography>
	);

	const optionsList = (ele: IAdvancedFilterValueOptions[]) => (
		<List
			height={200} // Height of the list container
			itemCount={ele?.length || 0} // Number of items in the list
			itemSize={DYNAMIC_LIST_ITEM_SIZE}
			width="100%" // Width of the list container
			itemData={ele} // Pass the data to the item renderer
		>
			{(listProps) => (
				<AdvancedFilterListItems
					{...listProps}
					handleOptionChange={handleChange}
					selectedOptionsLabels={selectedOptionsLabels}
					selectedOptionsLabelTypes={selectedOptionsLabelsType}
				/>
			)}
		</List>
	);

	const detailOptionsElement = (
		res: IAdvancedFiltersOptions,
		index: number,
	) => (
		<div key={res?.filterKey}>
			{!hideSearchFieldKeys.includes(res.filterKey) && (
				<ListSubheader
					className={styles['advancedfilters-search-fieldlistheader']}
				>
					{renderTextFieldSearch(res, index)}
				</ListSubheader>
			)}
			{res?.isLoading && <Loader show />}
			{res?.searchText &&
				res?.searchText.length > 0 &&
				res?.searchText.length < 3 && (
					<MenuItem
						className={styles['advancedfilters-accordion-nooptions-found']}
					>
						{toSentenceCase(MINIMUM_CHARACTER_MESSAGE)}
					</MenuItem>
				)}
			{res?.filterValues.length === 0 && res?.searchFlag && !res?.isLoading && (
				<MenuItem
					className={styles['advancedfilters-accordion-nooptions-found']}
				>
					{toSentenceCase(NO_OPTIONS_MESSAGE)}
				</MenuItem>
			)}
			<div
				className={renderClassName(res)}
				data-testid="advance-filter-scroll-block"
			>
				{res.selectedOptions &&
				res.selectedOptions.length > 0 &&
				res.filterValues.length === 0
					? optionsList(res.selectedOptions)
					: optionsList(res.filterValues)}
			</div>
		</div>
	);

	const detailElement = (ele: IAdvancedFiltersOptions, index: number) => (
		<Grid container justifyContent="space-between" display="flex">
			<Grid
				item
				xs={12}
				mt={1}
				lg={12}
				md={12}
				sm={12}
				display="flex"
				flexDirection="column"
				data-testid="advance-filter-textfield-block"
			>
				{ele.subOptions
					? ele.subOptions.map((res) => (
							<AccordionComp
								className={styles['advancedfilters-inner-accordians']}
								dataTestId="advancedfilter-inner-accordian-options"
								expanded={selectedSubLabels.includes(res?.filterTitle)}
								onChange={(event) => handleInnerAccordians(event, res)}
								key={`${res?.filterKey}${res?.filterTitle}`}
								summary={summaryElement(res)}
								details={detailOptionsElement(res, index)}
							/>
					  ))
					: detailOptionsElement(ele, index)}
			</Grid>
		</Grid>
	);

	const filterAccordian = () => (
		<Grid item xs={12} mt={1} mb={1} lg={12} md={12} sm={12}>
			<Grid
				xs={12}
				mt={1}
				lg={12}
				md={12}
				sm={12}
				display="flex"
				gap="10px"
				flexDirection="column"
				data-testid="advancedfilters-accordion-options"
			>
				{allOptions &&
					allOptions.map((ele: IAdvancedFiltersOptions, index: number) => (
						<AccordionComp
							className={styles['advancedfilters-accordian-header']}
							dataTestId="advancedfilter-options-accordion"
							expanded={selectedLabels.includes(ele?.filterKey)}
							onChange={(event) => handleAccordianChange(event, ele)}
							key={ele?.filterKey}
							summary={summaryElement(ele)}
							details={
								customDateDetailKeys.includes(ele.filterKey) ? (
									<CustomDateRangeComponent
										data={ele}
										title={ele.filterKey}
										onDateRangeChange={() => {}}
										onSelectDateRange={onSelectDateRange}
									/>
								) : (
									detailElement(ele, index)
								)
							}
						/>
					))}
			</Grid>
		</Grid>
	);

	const filterTopComponent = () => (
		<Grid
			item
			xs={12}
			lg={12}
			md={12}
			sm={12}
			className={styles['advancedfilters-header-btns-layout']}
		>
			<Grid
				item
				xs={12}
				lg={12}
				md={12}
				sm={12}
				mt={2}
				className={styles['advancedfilters-header-btns']}
			>
				<ButtonComp
					btnType="secondary"
					clickHandler={onClearFilterValues}
					data-testid="clear-allbtn"
					size="small"
					disabled={
						selectedOptions.length === 0 &&
						!applyFilterEnabled &&
						!assignedToMeChecked &&
						!isChanged
					}
				>
					{BUTTON_LABELS.CLEAR_ALL}
				</ButtonComp>
				<div className={styles['advancedfilters-clearall-icon-container']}>
					<AllCloseIcon
						stroke={imageColor}
						className={styles['advancedfilters-clearall-icon']}
						data-testid="advancedfilters-clearall-icon"
						onClick={onAllAccordionsClose}
					/>
				</div>
			</Grid>
		</Grid>
	);

	const filterBottomComponent = () => (
		<>
			<div className={styles['advancedfilters-assign-to-me-checkbox']}>
				{moduleType && (
					<div className={styles['advancedfilters-assign-to-me-checkbox']}>
						<Checkbox
							size="small"
							data-testid="assigned-to-me-checkbox"
							sx={{
								color: checkboxUncheckedColor,
								'&.Mui-checked': { color: checkboxCheckedColor },
							}}
							checked={assignedToMeChecked}
							onChange={handleAssignedToMeChecked}
						/>
						<Typography>
							{customShowOnlyAssignedMessage ?? 'Show only assigned to me'}
						</Typography>
					</div>
				)}
			</div>
			<div className={styles['advancedfilters-buttons-wrapper']}>
				<ButtonComp
					btnType="secondary"
					clickHandler={handleCancel}
					data-testid="cancel-btn"
					size="small"
				>
					{BUTTON_LABELS.CANCEL}
				</ButtonComp>
				<ButtonComp
					btnType="primary"
					clickHandler={onApplyFilter}
					data-testid="applyfilter-btn"
					size="small"
					disabled={
						selectedOptions.length === 0 &&
						!applyFilterEnabled &&
						!assignedToMeChecked &&
						!isChanged
					}
				>
					{BUTTON_LABELS.APPLY}
				</ButtonComp>
			</div>
		</>
	);

	return (
		<Grid
			container
			role="presentation"
			className={styles['advancedfilters-container']}
			data-testid="advanced-filters-container"
		>
			<Grid
				item
				xs={12}
				lg={12}
				md={12}
				sm={12}
				className={styles['advancedfilters-header']}
			>
				<Typography
					className={styles['advancedfilters-header-title']}
					data-testid="advancedfilter-text"
				>
					{FILTER_LABELS.ADVANCED_FILTERS}&nbsp;
					{returnCount()}
				</Typography>
				<IconButton size="medium" onClick={onClose} data-testid="close-filters">
					<DrawerClose fill={drawerCloseIconColor} />
				</IconButton>
			</Grid>
			{!filterDataLoading ? (
				<Grid
					item
					xs={12}
					lg={12}
					md={12}
					sm={12}
					className={styles['advancedfilters-content']}
				>
					{dividerRow()}
					<Grid
						item
						xs={12}
						lg={12}
						md={12}
						sm={12}
						className={styles['advancedfilters-body-container']}
					>
						{filterTopComponent()}
						{filterAccordian()}
					</Grid>
				</Grid>
			) : (
				<Stack className={styles['advancedfilters-loader']}>
					<Loader show />
				</Stack>
			)}
			<Grid
				item
				xs={12}
				lg={12}
				md={12}
				sm={12}
				className={styles['advancedfilters-footer']}
			>
				{dividerRow()}
				<Grid
					item
					xs={12}
					lg={12}
					md={12}
					sm={12}
					mt={2}
					className={styles['advancedfilters-footer-btn']}
				>
					<Grid container alignItems="center" justifyContent="space-between">
						{filterBottomComponent()}
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};
export default AdvancedFilters;
